// src/services/frontend-bundler/bundler.service.ts
import { 
  Connection, 
  PublicKey, 
  Keypair,
  Transaction,
  SystemProgram,
  LAMPORTS_PER_SOL,
  VersionedTransaction,
  TransactionMessage,
  ComputeBudgetProgram
} from '@solana/web3.js';
import { StorageService } from './storage.service';
import { EncryptionService } from './encryption.service';
import { TradingService } from './trading.service';
import bs58 from 'bs58';
import phantomDeeplinkService from '../phantom-deeplink.service';


export interface BundlerWallet {
  id: string;
  address: string;
  encryptedPrivateKey: string;
  balance: number;
  funded: boolean;
  active: boolean;
  createdAt: Date;
  lastUsed?: Date;
}

export interface Bundle {
  id: string;
  name: string;
  owner: string;
  wallets: BundlerWallet[];
  totalBalance: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  settings: BundleSettings;
}

export interface BundleSettings {
  autoRebalance: boolean;
  maxSlippage: number;
  priorityFee: number;
  distributeEvenly: boolean;
  stealthMode: boolean;
}

export class FrontendBundlerService {
  private connection: Connection;
  private storageService: StorageService;
  private encryptionService: EncryptionService;
  private tradingService: TradingService;
  private currentBundle: Bundle | null = null;
  private decryptedWallets: Map<string, Keypair> = new Map();
  private isLocked: boolean = true;
  private inactivityTimer: any;
  private currentPassword: string | null = null;

  constructor() {
    // Use InstantNodes RPC with optimized settings
    const rpcUrl = process.env.REACT_APP_RPC_URL || 
      'https://mainnet.helius-rpc.com/?api-key=37327d2d-9742-4fc4-b0d0-57d489b78697';
    
    this.connection = new Connection(rpcUrl, {
      commitment: 'processed',
      confirmTransactionInitialTimeout: 30000,
      disableRetryOnRateLimit: false,
      httpHeaders: {
        'Cache-Control': 'no-cache'
      }
    });

    this.storageService = new StorageService();
    this.encryptionService = new EncryptionService();
    this.tradingService = new TradingService(this.connection);
    
    this.initializeService();
  }

  private async initializeService() {
    await this.storageService.initialize();
    
    // Try to restore from session
    const restoredBundle = await this.restoreSessionBundle();
    if (restoredBundle) {
      console.log(`Restored bundle: ${restoredBundle.name}`);
    }
    
    this.startInactivityTimer();
  }

  /**
   * Save decrypted wallets to session storage (encrypted with a session key)
   */
  private async saveSessionData(bundleId: string, wallets: Map<string, Keypair>): Promise<void> {
    try {
      // Create a session key (valid for this browser session only)
      const sessionKey = this.getOrCreateSessionKey();
      
      // Convert wallets map to serializable format
      const walletsData: any[] = [];
      wallets.forEach((keypair, address) => {
        walletsData.push({
          address,
          secretKey: bs58.encode(keypair.secretKey)
        });
      });
      
      // Encrypt with session key for extra security
      const encrypted = await this.encryptionService.encrypt(
        JSON.stringify(walletsData),
        sessionKey
      );
      
      // Store in sessionStorage (cleared when browser closes)
      sessionStorage.setItem(`bundle_session_${bundleId}`, encrypted);
      sessionStorage.setItem(`bundle_active_${bundleId}`, 'true');
      sessionStorage.setItem('current_bundle_id', bundleId);
      
    } catch (error) {
      console.error('Failed to save session data:', error);
    }
  }

  /**
   * Restore bundle from session if available
   */
  async restoreSessionBundle(): Promise<Bundle | null> {
    try {
      const currentBundleId = sessionStorage.getItem('current_bundle_id');
      if (!currentBundleId) return null;
      
      const sessionData = sessionStorage.getItem(`bundle_session_${currentBundleId}`);
      const isActive = sessionStorage.getItem(`bundle_active_${currentBundleId}`);
      
      if (!sessionData || isActive !== 'true') return null;
      
      // Get bundle from storage
      const bundle = await this.storageService.getBundle(currentBundleId);
      if (!bundle) return null;
      
      // Decrypt session data
      const sessionKey = this.getOrCreateSessionKey();
      const decrypted = await this.encryptionService.decrypt(sessionData, sessionKey);
      const walletsData = JSON.parse(decrypted);
      
      // Restore wallets to memory
      this.decryptedWallets.clear();
      for (const walletData of walletsData) {
        const keypair = Keypair.fromSecretKey(bs58.decode(walletData.secretKey));
        this.decryptedWallets.set(walletData.address, keypair);
      }
      
      this.currentBundle = bundle;
      this.isLocked = false;
      this.resetInactivityTimer();
      
      // Refresh balances
      await this.refreshBalances();
      
      console.log('✅ Bundle restored from session');
      return bundle;
      
    } catch (error) {
      console.error('Failed to restore session bundle:', error);
      // Clear corrupted session data
      this.clearSessionData();
      return null;
    }
  }

  /**
   * Get or create a session-specific encryption key
   */
  private getOrCreateSessionKey(): string {
    let sessionKey = sessionStorage.getItem('session_key');
    if (!sessionKey) {
      // Generate a random session key
      sessionKey = bs58.encode(Keypair.generate().secretKey).substring(0, 32);
      sessionStorage.setItem('session_key', sessionKey);
    }
    return sessionKey;
  }

  /**
   * Clear all session data
   */
  private clearSessionData(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith('bundle_') || key === 'current_bundle_id' || key === 'session_key')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }

  /**
   * Get current bundle (for component access)
   */
  getCurrentBundle(): Bundle | null {
    return this.currentBundle;
  }

  /**
   * Check if bundle is locked
   */
  getIsLocked(): boolean {
    return this.isLocked;
  }

  async createBundle(
    name: string,
    owner: string,
    walletCount: number,
    password: string,
    settings?: Partial<BundleSettings>
  ): Promise<Bundle> {
    if (password.length < 4) {
      throw new Error('Password must be at least 4 characters');
    }

    const bundleId = this.generateBundleId();
    const wallets: BundlerWallet[] = [];

    // Generate wallets and encrypt private keys
    for (let i = 0; i < walletCount; i++) {
      const keypair = Keypair.generate();
      const encryptedPrivateKey = await this.encryptionService.encrypt(
        bs58.encode(keypair.secretKey),
        password
      );

      const wallet: BundlerWallet = {
        id: `${bundleId}-wallet-${i}`,
        address: keypair.publicKey.toString(),
        encryptedPrivateKey,
        balance: 0,
        funded: false,
        active: true,
        createdAt: new Date()
      };

      wallets.push(wallet);
      // Store decrypted keypair in memory for current session
      this.decryptedWallets.set(wallet.address, keypair);
    }

    const bundle: Bundle = {
      id: bundleId,
      name,
      owner,
      wallets,
      totalBalance: 0,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      settings: {
        autoRebalance: false,
        maxSlippage: 5,
        priorityFee: 0.0001,
        distributeEvenly: true,
        stealthMode: false,
        ...settings
      }
    };

    // Save to IndexedDB
    await this.storageService.saveBundle(bundle);
    this.currentBundle = bundle;
    this.isLocked = false;
    this.resetInactivityTimer();
    
    // Save to session for persistence
    await this.saveSessionData(bundleId, this.decryptedWallets);

    return bundle;
  }

  /**
 * Execute bundle purchase specifically for Pump.fun tokens using PumpPortal API
 */
async executePumpFunBundlePurchase(
  tokenAddress: string,
  buyAmountPerWallet: number,
  slippage: number,
  onProgress?: (progress: number, status: string) => void
): Promise<{
  success: boolean;
  successful: number;
  failed: number;
  transactions: string[];
  error?: string;
}> {
  if (!this.currentBundle || this.isLocked) {
    throw new Error('No unlocked bundle available');
  }

  const results: string[] = [];
  let successful = 0;
  let failed = 0;
  const wallets = this.currentBundle.wallets;

  console.log(`🚀 Starting Pump.fun bundle buy for ${wallets.length} wallets`);

  for (let i = 0; i < wallets.length; i++) {
    const wallet = wallets[i];
    
    // Check balance
    if (wallet.balance < buyAmountPerWallet + 0.002) {
      console.log(`⚠️ Wallet ${i + 1}/${wallets.length} insufficient balance`);
      failed++;
      if (onProgress) {
        onProgress(
          i + 1,
          `Wallet ${i + 1}/${wallets.length}: Insufficient balance`
        );
      }
      continue;
    }

    if (onProgress) {
      onProgress(i + 1, `Processing wallet ${i + 1}/${wallets.length}...`);
    }

    // Decrypt the private key
    const decryptedKey = await this.encryptionService.decrypt(
      wallet.encryptedPrivateKey,
      this.currentPassword!
    );
    const keypair = Keypair.fromSecretKey(bs58.decode(decryptedKey));

    // Attempt buy with retries
    let attempts = 0;
    const maxAttempts = 2;
    let buySuccess = false;

    while (attempts < maxAttempts && !buySuccess) {
      attempts++;
      
      try {
        // Use PumpPortal API for Pump.fun tokens
        const response = await fetch('https://pumpportal.fun/api/trade-local', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            publicKey: keypair.publicKey.toBase58(),
            action: 'buy',
            mint: tokenAddress,
            denominatedInSol: 'true',
            amount: buyAmountPerWallet,
            slippage: slippage,
            priorityFee: 0.0001,
            pool: 'pump', // Force pump.fun bonding curve
          }),
        });

        if (!response.ok) {
          throw new Error(`PumpPortal API error: ${response.status}`);
        }

        const data = await response.arrayBuffer();
        const tx = VersionedTransaction.deserialize(new Uint8Array(data));
        
        // Sign the transaction
        tx.sign([keypair]);

        // Send the transaction
        const signature = await this.connection.sendTransaction(tx, {
          skipPreflight: false,
          maxRetries: 3,
        });

        // Confirm
        await this.connection.confirmTransaction(signature, 'confirmed');

        buySuccess = true;
        successful++;
        results.push(signature);
        console.log(`✅ Wallet ${i + 1}/${wallets.length} succeeded: ${signature}`);

      } catch (error: any) {
        console.log(`❌ Wallet ${i + 1}/${wallets.length} attempt ${attempts} failed: ${error.message}`);
        
        if (attempts >= maxAttempts) {
          failed++;
        } else {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // Small delay between wallets
    if (i < wallets.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  const successRate = (successful / wallets.length) * 100;
  console.log(`\n📊 Pump.fun Bundle Buy Results:`);
  console.log(`   Successful: ${successful}/${wallets.length}`);
  console.log(`   Failed: ${failed}/${wallets.length}`);
  console.log(`   Success Rate: ${successRate.toFixed(1)}%`);

  if (onProgress) {
    onProgress(
      wallets.length,
      `Complete: ${successful} success, ${failed} failed`
    );
  }

  const overallSuccess = successRate >= 50;

  return {
    success: overallSuccess,
    successful,
    failed,
    transactions: results,
    error: overallSuccess ? undefined : `Low success rate: ${successRate.toFixed(1)}%`,
  };
}

  async fundWalletsBatched(
    mainWallet: PublicKey,
    amountPerWallet: number,
    signAll: (txs: Transaction[]) => Promise<Transaction[]>,
    delay: number = 2000,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; funded: number; failed: number; error?: string }> {
    if (!this.currentBundle) throw new Error('No active bundle');
    if (this.isLocked) throw new Error('Bundle is locked. Please unlock first.');

    const wallets = this.currentBundle.wallets;
    const lamportsPerWallet = amountPerWallet * LAMPORTS_PER_SOL;
    
    // Check balance
    const balance = await this.connection.getBalance(mainWallet);
    const estFeesPerTxLamports = Math.floor(0.001 * LAMPORTS_PER_SOL);
    const BATCH_SIZE = 20;
    const numBatches = Math.ceil(wallets.length / BATCH_SIZE);
    const totalNeeded = (wallets.length * lamportsPerWallet) + (estFeesPerTxLamports * numBatches);
    
    if (balance < totalNeeded) {
      throw new Error(`Insufficient balance. Need ${(totalNeeded / LAMPORTS_PER_SOL).toFixed(4)} SOL`);
    }

    let funded = 0;
    let failed = 0;
    const transactions: Transaction[] = [];
    
    // Create batched transactions
    for (let i = 0; i < wallets.length; i += BATCH_SIZE) {
      const batch = wallets.slice(i, Math.min(i + BATCH_SIZE, wallets.length));
      const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash('confirmed');
      
      const tx = new Transaction();
      
      tx.add(
        ComputeBudgetProgram.setComputeUnitPrice({
          microLamports: 100,  // ✅ Much more reasonable
        })
      );
      
      for (const wallet of batch) {
        tx.add(
          SystemProgram.transfer({
            fromPubkey: mainWallet,
            toPubkey: new PublicKey(wallet.address),
            lamports: lamportsPerWallet,
          })
        );
      }
      
      tx.feePayer = mainWallet;
      tx.recentBlockhash = blockhash;
      transactions.push(tx);
    }
    
    console.log(`Created ${transactions.length} transactions for ${wallets.length} wallets`);
    
    const signedTxs = await signAll(transactions);
    
    for (let i = 0; i < signedTxs.length; i++) {
      try {
        const signature = await this.connection.sendRawTransaction(
          signedTxs[i].serialize(),
          {
            skipPreflight: false,
            preflightCommitment: 'confirmed',
            maxRetries: 3
          }
        );
        
        console.log(`Batch ${i + 1}/${signedTxs.length} sent: ${signature}`);
        
        const batchStart = i * BATCH_SIZE;
        const batchEnd = Math.min(batchStart + BATCH_SIZE, wallets.length);
        
        const confirmed = await Promise.race([
          this.connection.confirmTransaction(signature, 'confirmed'),
          new Promise(resolve => setTimeout(() => resolve(false), 5000))
        ]);
        
        for (let j = batchStart; j < batchEnd; j++) {
          wallets[j].funded = true;
          wallets[j].balance = amountPerWallet;
          funded++;
        }
        
        console.log(`Funded wallets ${batchStart + 1}-${batchEnd} of ${wallets.length}`);
        
        if (onProgress) {
          onProgress((funded / wallets.length) * 100);
        }
        
        if (i < signedTxs.length - 1 && this.currentBundle?.settings?.stealthMode) {
          await new Promise(res => setTimeout(res, delay * (0.5 + Math.random())));
        }
        
      } catch (error: any) {
        console.error(`Batch ${i + 1} failed:`, error.message);
        const batchSize = Math.min(BATCH_SIZE, wallets.length - (i * BATCH_SIZE));
        failed += batchSize;
      }
    }
    
    if (this.currentBundle) {
      this.currentBundle.totalBalance = funded * amountPerWallet;
      this.currentBundle.updatedAt = new Date();
      await this.storageService.updateBundle(this.currentBundle);
    }
    
    return { success: funded > 0, funded, failed };
  }

  async fundWalletsSequential(
    mainWallet: PublicKey,
    amountPerWallet: number,
    signTransaction: (tx: Transaction) => Promise<Transaction>,
    delay: number = 2000,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; funded: number; failed: number; error?: string }> {
    if (!this.currentBundle) throw new Error('No active bundle');
    if (this.isLocked) throw new Error('Bundle is locked. Please unlock first.');

    const wallets = this.currentBundle.wallets;
    const lamportsPerWallet = amountPerWallet * LAMPORTS_PER_SOL;
    
    let funded = 0;
    let failed = 0;

    for (let i = 0; i < wallets.length; i++) {
      try {
        const wallet = wallets[i];
        const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash('finalized');
        
        const transaction = new Transaction();
        
        if (this.currentBundle.settings?.priorityFee > 0) {
          transaction.add(
            ComputeBudgetProgram.setComputeUnitPrice({
              microLamports: Math.floor(this.currentBundle.settings.priorityFee * 1_000_000)
            })
          );
        }

        transaction.add(
          SystemProgram.transfer({
            fromPubkey: mainWallet,
            toPubkey: new PublicKey(wallet.address),
            lamports: lamportsPerWallet
          })
        );

        transaction.recentBlockhash = blockhash;
        transaction.feePayer = mainWallet;

        const signedTx = await signTransaction(transaction);
        const signature = await this.connection.sendRawTransaction(signedTx.serialize(), {
          skipPreflight: false,
          preflightCommitment: 'processed'
        });
        
        await Promise.race([
          this.connection.confirmTransaction({ 
            signature, 
            blockhash, 
            lastValidBlockHeight 
          }, 'confirmed'),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Confirmation timeout')), 30000)
          )
        ]);
        
        wallet.funded = true;
        wallet.balance = amountPerWallet;
        funded++;
        
        console.log(`Funded wallet ${i + 1}/${wallets.length}: ${wallet.address}`);
        
        if (onProgress) {
          onProgress(((i + 1) / wallets.length) * 100);
        }
        
        if (i < wallets.length - 1) {
          if (this.currentBundle.settings?.stealthMode) {
            await new Promise(res => setTimeout(res, delay * (0.5 + Math.random())));
          } else {
            await new Promise(res => setTimeout(res, 500));
          }
        }
        
      } catch (error: any) {
        console.error(`Failed to fund wallet ${i + 1}:`, error.message);
        failed++;
      }
    }

    this.currentBundle.totalBalance = funded * amountPerWallet;
    this.currentBundle.updatedAt = new Date();
    await this.storageService.updateBundle(this.currentBundle);

    return { success: funded > 0, funded, failed };
  }

  async fundWallets(
    mainWallet: PublicKey,
    amountPerWallet: number,
    signTransaction: (tx: Transaction) => Promise<Transaction>,
    delay: number = 2000,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; funded: number; failed: number; error?: string }> {
    if (!this.currentBundle) {
      throw new Error('No active bundle');
    }

    if (this.isLocked) {
      throw new Error('Bundle is locked. Please unlock first.');
    }

    let funded = 0;
    let failed = 0;

    try {
      const lamportsPerWallet = amountPerWallet * LAMPORTS_PER_SOL;
      const wallets = this.currentBundle.wallets;
      
      const balance = await this.connection.getBalance(mainWallet);
      const totalNeeded = (lamportsPerWallet * wallets.length) + (0.001 * LAMPORTS_PER_SOL * wallets.length);
      
      if (balance < totalNeeded) {
        throw new Error(`Insufficient balance. Need ${totalNeeded / LAMPORTS_PER_SOL} SOL`);
      }

      for (let i = 0; i < wallets.length; i++) {
        try {
          const wallet = wallets[i];
          
          if (this.currentBundle.settings.stealthMode && i > 0) {
            const randomDelay = delay * (1 + Math.random() * 2);
            await this.sleep(randomDelay);
          }

          const transaction = new Transaction();
          
          if (this.currentBundle.settings.priorityFee > 0) {
            transaction.add(
              ComputeBudgetProgram.setComputeUnitPrice({
                microLamports: Math.floor(this.currentBundle.settings.priorityFee * 1000000)
              })
            );
          }

          transaction.add(
            SystemProgram.transfer({
              fromPubkey: mainWallet,
              toPubkey: new PublicKey(wallet.address),
              lamports: lamportsPerWallet
            })
          );

          const { blockhash } = await this.connection.getLatestBlockhash();
          transaction.recentBlockhash = blockhash;
          transaction.feePayer = mainWallet;

          const signedTx = await signTransaction(transaction);
          const signature = await this.connection.sendRawTransaction(signedTx.serialize());
          
          await this.connection.confirmTransaction(signature, 'confirmed');
          
          wallet.funded = true;
          wallet.balance = amountPerWallet;
          funded++;

          if (onProgress) {
            onProgress((funded / wallets.length) * 100);
          }
        } catch (error) {
          console.error(`Failed to fund wallet ${i}:`, error);
          failed++;
        }
      }

      this.currentBundle.totalBalance = funded * amountPerWallet;
      this.currentBundle.updatedAt = new Date();
      await this.storageService.updateBundle(this.currentBundle);

      return { success: funded > 0, funded, failed };
    } catch (error: any) {
      return { success: false, funded, failed, error: error.message };
    }
  }

 

// Add this method to handle mobile deeplink funding
async fundWalletsWithPhantomMobile(
  mainWallet: PublicKey,
  amountPerWallet: number,
  delay: number = 2000,
  onProgress?: (progress: number) => void
): Promise<{ success: boolean; funded: number; failed: number; error?: string }> {
  if (!this.currentBundle) throw new Error('No active bundle');
  if (this.isLocked) throw new Error('Bundle is locked. Please unlock first.');

  const wallets = this.currentBundle.wallets;
  const lamportsPerWallet = amountPerWallet * LAMPORTS_PER_SOL;
  
  // Check balance
  const balance = await this.connection.getBalance(mainWallet);
  const estFeesPerTxLamports = Math.floor(0.001 * LAMPORTS_PER_SOL);
  const BATCH_SIZE = 20;
  const numBatches = Math.ceil(wallets.length / BATCH_SIZE);
  const totalNeeded = (wallets.length * lamportsPerWallet) + (estFeesPerTxLamports * numBatches);
  
  if (balance < totalNeeded) {
    throw new Error(`Insufficient balance. Need ${(totalNeeded / LAMPORTS_PER_SOL).toFixed(4)} SOL`);
  }

  // Store funding state for recovery after redirect
  const fundingState = {
    bundleId: this.currentBundle.id,
    mainWallet: mainWallet.toBase58(),
    amountPerWallet,
    totalBatches: numBatches,
    currentBatch: 0,
    funded: 0,
    failed: 0,
    timestamp: Date.now(),
    type: 'bundle_funding',
    walletAddresses: wallets.map(w => w.address)
  };
  
  localStorage.setItem('phantom_funding_state', JSON.stringify(fundingState));

  // Process batches one at a time for mobile
  let funded = 0;
  let failed = 0;

  for (let batchIndex = 0; batchIndex < numBatches; batchIndex++) {
    const start = batchIndex * BATCH_SIZE;
    const end = Math.min(start + BATCH_SIZE, wallets.length);
    const batch = wallets.slice(start, end);
    
    try {
      // Create transaction for this batch
      const { blockhash } = await this.connection.getLatestBlockhash('confirmed');
      
      const transaction = new Transaction();
      
      // Add priority fee
      transaction.add(
        ComputeBudgetProgram.setComputeUnitPrice({
          microLamports: 100,
        })
      );
      
      // Add all transfers for this batch
      for (const wallet of batch) {
        transaction.add(
          SystemProgram.transfer({
            fromPubkey: mainWallet,
            toPubkey: new PublicKey(wallet.address),
            lamports: lamportsPerWallet,
          })
        );
      }
      
      transaction.feePayer = mainWallet;
      transaction.recentBlockhash = blockhash;
      
      // Sign transaction using Phantom deeplink
      console.log(`Mobile: Signing batch ${batchIndex + 1}/${numBatches} with Phantom deeplink`);
      
      // This will redirect to Phantom app
      const signature = await phantomDeeplinkService.signTransaction(transaction);
      
      // Note: The code below won't execute due to redirect
      // The actual sending should happen in a redirect handler
      
    } catch (error: any) {
      // If we're here, it might be a redirect or an actual error
      if (error.message?.includes('Redirecting')) {
        // This is expected for mobile deeplink
        console.log('Redirecting to Phantom for signing...');
        // The rest will be handled when the user returns
        break;
      } else {
        console.error(`Batch ${batchIndex + 1} failed:`, error);
        failed += batch.length;
      }
    }
  }

  // For mobile, we'll return a partial result and handle completion on redirect
  return { 
    success: false, 
    funded: 0, 
    failed: 0, 
    error: 'Redirecting to Phantom for signing...' 
  };
}

// Add this method to handle the redirect return from Phantom
async handlePhantomFundingRedirect(): Promise<{
  success: boolean;
  funded: number;
  failed: number;
  error?: string;
}> {
  try {
    // Check if we're returning from a signing redirect
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('data') || !urlParams.has('nonce')) {
      return { success: false, funded: 0, failed: 0, error: 'Not a signing redirect' };
    }

    // Handle the signing redirect
    const signature = await phantomDeeplinkService.handleSigningRedirect();
    
    if (!signature) {
      throw new Error('No signature received from Phantom');
    }
    
    // Get funding state
    const fundingStateStr = localStorage.getItem('phantom_funding_state');
    if (!fundingStateStr) {
      throw new Error('Funding state lost');
    }
    
    const fundingState = JSON.parse(fundingStateStr);
    
    // Get the pending transaction
    const pendingTx = localStorage.getItem('phantom_pending_transaction');
    if (!pendingTx) {
      throw new Error('Pending transaction lost');
    }
    
    const pendingData = JSON.parse(pendingTx);
    const transaction = Transaction.from(bs58.decode(pendingData.transaction));
    
    // Add the signature to the transaction
    const mainWallet = new PublicKey(fundingState.mainWallet);
    transaction.addSignature(mainWallet, Buffer.from(bs58.decode(signature)));
    
    // Send the signed transaction
    const txSignature = await this.connection.sendRawTransaction(
      transaction.serialize(),
      {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
        maxRetries: 3
      }
    );
    
    console.log(`Transaction sent: ${txSignature}`);
    
    // Wait for confirmation
    const confirmed = await Promise.race([
      this.connection.confirmTransaction(txSignature, 'confirmed'),
      new Promise(resolve => setTimeout(() => resolve(false), 30000))
    ]);
    
    if (!confirmed) {
      throw new Error('Transaction confirmation timeout');
    }
    
    // Update funding state
    const BATCH_SIZE = 20;
    const currentBatch = fundingState.currentBatch;
    const start = currentBatch * BATCH_SIZE;
    const end = Math.min(start + BATCH_SIZE, fundingState.walletAddresses.length);
    const batchSize = end - start;
    
    fundingState.funded += batchSize;
    fundingState.currentBatch += 1;
    
    // Update wallet states
    if (this.currentBundle) {
      for (let i = start; i < end; i++) {
        this.currentBundle.wallets[i].funded = true;
        this.currentBundle.wallets[i].balance = fundingState.amountPerWallet;
      }
      
      this.currentBundle.totalBalance = fundingState.funded * fundingState.amountPerWallet;
      this.currentBundle.updatedAt = new Date();
      await this.storageService.updateBundle(this.currentBundle);
    }
    
    console.log(`✅ Funded wallets ${start + 1}-${end} of ${fundingState.walletAddresses.length}`);
    
    // Check if there are more batches
    if (fundingState.currentBatch < fundingState.totalBatches) {
      // Save updated state for next batch
      localStorage.setItem('phantom_funding_state', JSON.stringify(fundingState));
      
      // Continue with next batch
      console.log('More batches to process, continuing...');
      
      // You might want to automatically trigger the next batch here
      // or show a UI to let the user continue
      
      return {
        success: true,
        funded: fundingState.funded,
        failed: fundingState.failed,
        error: 'Batch complete, more batches remaining'
      };
    } else {
      // All batches complete
      localStorage.removeItem('phantom_funding_state');
      localStorage.removeItem('phantom_pending_transaction');
      
      return {
        success: true,
        funded: fundingState.funded,
        failed: fundingState.failed
      };
    }
    
  } catch (error: any) {
    console.error('Funding redirect handling failed:', error);
    
    // Clean up state
    localStorage.removeItem('phantom_funding_state');
    localStorage.removeItem('phantom_pending_transaction');
    
    return {
      success: false,
      funded: 0,
      failed: 1,
      error: error.message
    };
  }
}

// Add helper method to check if we're returning from a funding flow
isFundingRedirect(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const hasFundingState = localStorage.getItem('phantom_funding_state') !== null;
  const hasSigningParams = urlParams.has('data') && urlParams.has('nonce');
  
  return hasFundingState && hasSigningParams;
}

// Add method to resume funding after redirect
async resumeFundingAfterRedirect(): Promise<void> {
  if (!this.isFundingRedirect()) return;
  
  try {
    const result = await this.handlePhantomFundingRedirect();
    
    if (result.success) {
      // Check if more batches are needed
      const fundingStateStr = localStorage.getItem('phantom_funding_state');
      if (fundingStateStr) {
        const fundingState = JSON.parse(fundingStateStr);
        if (fundingState.currentBatch < fundingState.totalBatches) {
          // Continue with next batch
          console.log('Continuing with next batch...');
          // You might want to show a UI prompt here
        }
      }
    }
  } catch (error) {
    console.error('Failed to resume funding:', error);
  }
}

async fundWalletsSingleTransaction(
  mainWallet: PublicKey,
  amountPerWallet: number,
  signTransaction: (tx: Transaction) => Promise<Transaction>,
  onProgress?: (progress: number) => void
): Promise<{ success: boolean; funded: number; failed: number; error?: string }> {
  if (!this.currentBundle) throw new Error('No active bundle');
  if (this.isLocked) throw new Error('Bundle is locked. Please unlock first.');

  const wallets = this.currentBundle.wallets;
  
  // CRITICAL FIX 1: Strict limit for mobile reliability
  const MAX_WALLETS_MOBILE = 5; // Reduced from whatever it was before
  const walletsToFund = wallets.slice(0, Math.min(wallets.length, MAX_WALLETS_MOBILE));
  
  const lamportsPerWallet = Math.floor(amountPerWallet * LAMPORTS_PER_SOL);
  
  console.log('📱 Mobile Single Transaction Funding:');
  console.log('  Wallets to fund:', walletsToFund.length, 'of', wallets.length);
  console.log('  Amount per wallet:', amountPerWallet, 'SOL');
  console.log('  Lamports per wallet:', lamportsPerWallet);
  
  // Check balance
  let balance: number;
  try {
    balance = await this.connection.getBalance(mainWallet);
    console.log('  Main wallet balance:', (balance / LAMPORTS_PER_SOL).toFixed(4), 'SOL');
  } catch (error) {
    console.error('Failed to check balance:', error);
    return {
      success: false,
      funded: 0,
      failed: wallets.length,
      error: 'Failed to check wallet balance'
    };
  }
  
  // CRITICAL FIX 2: More accurate fee calculation
  const FEE_PER_TRANSFER = 0.000005; // Per transfer fee
  const BASE_FEE = 0.0001; // Base transaction fee
  const totalFees = BASE_FEE + (FEE_PER_TRANSFER * walletsToFund.length);
  const totalFeeLamports = Math.ceil(totalFees * LAMPORTS_PER_SOL);
  const totalNeeded = (walletsToFund.length * lamportsPerWallet) + totalFeeLamports;
  
  console.log('  Base fee:', BASE_FEE, 'SOL');
  console.log('  Per-transfer fee:', FEE_PER_TRANSFER, 'SOL');
  console.log('  Total fees:', totalFees.toFixed(6), 'SOL');
  console.log('  Total needed:', (totalNeeded / LAMPORTS_PER_SOL).toFixed(4), 'SOL');
  
  if (balance < totalNeeded) {
    const shortfall = (totalNeeded - balance) / LAMPORTS_PER_SOL;
    return {
      success: false,
      funded: 0,
      failed: wallets.length,
      error: `Insufficient balance. You have ${(balance / LAMPORTS_PER_SOL).toFixed(4)} SOL but need ${(totalNeeded / LAMPORTS_PER_SOL).toFixed(4)} SOL (short by ${shortfall.toFixed(4)} SOL)`
    };
  }

  try {
    // CRITICAL FIX 3: Use 'confirmed' instead of 'finalized' for better compatibility
    console.log('Getting fresh blockhash...');
    const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash('confirmed');
    console.log('  Blockhash:', blockhash.substring(0, 20) + '...');
    console.log('  Block height:', lastValidBlockHeight);
    
    // Create a clean transaction
    const transaction = new Transaction();
    
    // Set fee payer and blockhash
    transaction.feePayer = mainWallet;
    transaction.recentBlockhash = blockhash;
    
    // CRITICAL: NO compute budget instructions for mobile
    
    // Add transfer instructions - only for walletsToFund, not all wallets
    console.log('Adding transfer instructions for', walletsToFund.length, 'wallets:');
    for (let i = 0; i < walletsToFund.length; i++) {
      const wallet = walletsToFund[i];
      const destPubkey = new PublicKey(wallet.address);
      
      console.log(`  ${i + 1}. Transfer ${amountPerWallet} SOL to ${wallet.address.substring(0, 8)}...`);
      
      transaction.add(
        SystemProgram.transfer({
          fromPubkey: mainWallet,
          toPubkey: destPubkey,
          lamports: lamportsPerWallet,
        })
      );
    }
    
    // CRITICAL FIX 4: More conservative size check
    const message = transaction.compileMessage();
    const messageBytes = message.serialize();
    console.log('Transaction compiled:');
    console.log('  Instructions:', transaction.instructions.length);
    console.log('  Message size:', messageBytes.length, 'bytes');
    console.log('  Signatures needed:', message.header.numRequiredSignatures);
    
    // Be more conservative with size limit
    if (messageBytes.length > 1000) { // Reduced from 1232 for safety margin
      const recommendedWallets = Math.floor(walletsToFund.length * (1000 / messageBytes.length));
      return {
        success: false,
        funded: 0,
        failed: wallets.length,
        error: `Transaction too large (${messageBytes.length} bytes). Try with ${recommendedWallets} wallets or less.`
      };
    }
    
    if (onProgress) {
      onProgress(25);
    }
    
    console.log('Requesting signature from wallet...');
    
    try {
      // This will redirect to Phantom on mobile
      const signedTx = await signTransaction(transaction);
      
      // Code below won't execute on mobile due to redirect
      
      if (onProgress) {
        onProgress(50);
      }
      
      // Send the signed transaction
      const signature = await this.connection.sendRawTransaction(
        signedTx.serialize(),
        {
          skipPreflight: true,
          preflightCommitment: 'processed',
          maxRetries: 3
        }
      );
      
      console.log(`Transaction sent: ${signature}`);
      
      if (onProgress) {
        onProgress(75);
      }
      
      // Wait for confirmation with timeout
      const confirmResult = await Promise.race([
        this.connection.confirmTransaction({
          signature,
          blockhash,
          lastValidBlockHeight
        }, 'confirmed'),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Confirmation timeout')), 30000)
        )
      ]) as any;
      
      if (confirmResult?.value?.err) {
        throw new Error(`Transaction failed: ${JSON.stringify(confirmResult.value.err)}`);
      }
      
      console.log('Transaction confirmed!');
      
      // Update only the funded wallets, not all wallets
      for (let i = 0; i < walletsToFund.length; i++) {
        walletsToFund[i].funded = true;
        walletsToFund[i].balance = amountPerWallet;
      }
      
      // Update bundle state
      this.currentBundle.totalBalance = walletsToFund.length * amountPerWallet;
      this.currentBundle.updatedAt = new Date();
      await this.storageService.updateBundle(this.currentBundle);
      
      if (onProgress) {
        onProgress(100);
      }
      
      console.log(`✅ Successfully funded ${walletsToFund.length} wallets in a single transaction`);
      
      // Return accurate counts
      return { 
        success: true, 
        funded: walletsToFund.length, 
        failed: wallets.length - walletsToFund.length // Any wallets we couldn't fund due to limit
      };
      
    } catch (signError: any) {
      // Check if it's a redirect (expected for mobile)
      if (signError.message?.includes('Redirect')) {
        console.log('Redirecting to Phantom for signing...');
        // Store state for after redirect - use walletsToFund.length, not wallets.length
        localStorage.setItem('pending_single_tx_funding', JSON.stringify({
          bundleId: this.currentBundle.id,
          walletCount: walletsToFund.length,
          amountPerWallet,
          timestamp: Date.now()
        }));
        throw signError; // Re-throw to let the component handle it
      }
      
      throw signError;
    }
    
  } catch (error: any) {
    console.error('Single transaction funding failed:', error);
    
    // Parse specific error types
    if (error.message?.includes('Redirect')) {
      // This is expected for mobile
      throw error;
    } else if (error.message?.includes('insufficient')) {
      return { 
        success: false, 
        funded: 0, 
        failed: wallets.length,
        error: 'Insufficient balance for transaction'
      };
    } else if (error.message?.includes('too large')) {
      return { 
        success: false, 
        funded: 0, 
        failed: wallets.length,
        error: `Transaction too large. Try funding fewer wallets at once.`
      };
    } else if (error.message?.includes('timeout')) {
      return { 
        success: false, 
        funded: 0, 
        failed: wallets.length,
        error: 'Transaction confirmation timeout. Please check your wallet.'
      };
    }
    
    return { 
      success: false, 
      funded: 0, 
      failed: wallets.length,
      error: error.message || 'Transaction failed' 
    };
  }
}

async handleSingleTxFundingRedirect(): Promise<{
  success: boolean;
  funded: number;
  failed: number;
  error?: string;
}> {
  try {
    // Get the pending funding state
    const pendingFundingStr = localStorage.getItem('pending_single_tx_funding');
    if (!pendingFundingStr) {
      throw new Error('No pending funding operation');
    }
    
    const pendingFunding = JSON.parse(pendingFundingStr);
    console.log('Resuming funding for bundle:', pendingFunding.bundleId);
    
    // Get the signed transaction from Phantom's response
    const pendingTxStr = localStorage.getItem('phantom_pending_transaction');
    if (!pendingTxStr) {
      throw new Error('No pending transaction found');
    }
    
    const pendingTx = JSON.parse(pendingTxStr);
    const signedTxBytes = bs58.decode(pendingTx.transaction);
    
    // Send the already-signed transaction
    console.log('Sending signed transaction to network...');
    const signature = await this.connection.sendRawTransaction(
      signedTxBytes,
      {
        skipPreflight: true,
        preflightCommitment: 'processed',
        maxRetries: 3
      }
    );
    
    console.log('Transaction sent:', signature);
    
    // Wait for confirmation
    const confirmation = await this.connection.confirmTransaction(signature, 'confirmed');
    
    if (confirmation.value.err) {
      throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
    }
    
    console.log('Transaction confirmed!');
    
    // Update wallet states
    if (this.currentBundle) {
      for (let i = 0; i < pendingFunding.walletCount; i++) {
        if (this.currentBundle.wallets[i]) {
          this.currentBundle.wallets[i].funded = true;
          this.currentBundle.wallets[i].balance = pendingFunding.amountPerWallet;
        }
      }
      
      this.currentBundle.totalBalance = pendingFunding.walletCount * pendingFunding.amountPerWallet;
      this.currentBundle.updatedAt = new Date();
      await this.storageService.updateBundle(this.currentBundle);
    }
    
    // Clean up
    localStorage.removeItem('pending_single_tx_funding');
    localStorage.removeItem('phantom_pending_transaction');
    
    console.log(`✅ Successfully funded ${pendingFunding.walletCount} wallets`);
    
    return {
      success: true,
      funded: pendingFunding.walletCount,
      failed: 0
    };
    
  } catch (error: any) {
    console.error('Failed to complete funding after redirect:', error);
    
    // Clean up
    localStorage.removeItem('pending_single_tx_funding');
    localStorage.removeItem('phantom_pending_transaction');
    
    return {
      success: false,
      funded: 0,
      failed: 1,
      error: error.message
    };
  }
}

// Add helper to check if we're returning from a single tx funding
isSingleTxFundingRedirect(): boolean {
  const hasPendingFunding = localStorage.getItem('pending_single_tx_funding') !== null;
  const urlParams = new URLSearchParams(window.location.search);
  const hasSigningParams = urlParams.has('data') && urlParams.has('nonce');
  
  return hasPendingFunding && hasSigningParams;
}

  async executeBundlePurchase(
  tokenMint: string,
  amountInSol: number,
  slippage: number = 5,
  onProgress?: (progress: number, status: string) => void
): Promise<{
  success: boolean;
  successful: number;
  failed: number;
  transactions: string[];
  error?: string;
}> {
  if (!this.currentBundle) {
    throw new Error('No active bundle');
  }

  if (this.isLocked) {
    throw new Error('Bundle is locked. Please unlock first.');
  }

  const transactions: string[] = [];
  let successful = 0;
  let failed = 0;
  const failedWallets: string[] = [];
  const successfulTxs: string[] = [];

  try {
    const TRANSACTION_FEE_BUFFER = 0.0008 // Increased buffer for fees
    const MIN_BALANCE = amountInSol + TRANSACTION_FEE_BUFFER;
    
    // Step 1: Refresh wallet balances before filtering
    await this.refreshWalletBalances();
    
    // Step 2: Filter eligible wallets with stricter criteria
    const activeWallets = this.currentBundle.wallets.filter(w => {
      const isEligible = w.funded && w.active && w.balance >= MIN_BALANCE;
      
      if (!isEligible) {
        console.log(`⚠️ Wallet ${w.address.slice(0,8)} skipped: 
          funded: ${w.funded}, 
          active: ${w.active}, 
          balance: ${w.balance.toFixed(4)}, 
          required: ${MIN_BALANCE.toFixed(4)}`);
      }
      
      return isEligible;
    });
    
    if (activeWallets.length === 0) {
      throw new Error('No wallets with sufficient balance available');
    }

    console.log(`🚀 Starting sequential bundle purchase for ${activeWallets.length} wallets`);
    
    if (onProgress) {
      onProgress(0, `Preparing ${activeWallets.length} transactions...`);
    }

    // Step 3: Execute transactions SEQUENTIALLY instead of concurrently
    for (let i = 0; i < activeWallets.length; i++) {
      const wallet = activeWallets[i];
      const progressPercent = (i / activeWallets.length) * 100;
      
      try {
        if (onProgress) {
          onProgress(progressPercent, `Processing wallet ${i + 1}/${activeWallets.length}...`);
        }

        const keypair = this.decryptedWallets.get(wallet.address);
        if (!keypair) {
          throw new Error(`Wallet not decrypted`);
        }

        // Double-check balance right before transaction
        const currentBalance = await this.connection.getBalance(keypair.publicKey);
        const balanceInSol = currentBalance / LAMPORTS_PER_SOL;
        
        if (balanceInSol < MIN_BALANCE) {
          throw new Error(`Insufficient balance: ${balanceInSol.toFixed(4)} SOL, need ${MIN_BALANCE.toFixed(4)} SOL`);
        }

        // Add randomized delay between transactions (stealth mode)
        if (i > 0) {
          const delayMs = 200 + Math.random() * 800; // 200-1000ms random delay
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }

        // Execute the swap with retry logic
        const result = await this.executeSwapWithRetry(
          keypair,
          tokenMint,
          amountInSol,
          slippage,
          3 // max retries
        );

        if (result.success && result.signature) {
          successful++;
          successfulTxs.push(result.signature);
          console.log(`✅ Wallet ${i + 1}/${activeWallets.length} success: ${result.signature}`);
        } else {
          failed++;
          failedWallets.push(wallet.address);
          console.error(`❌ Wallet ${i + 1}/${activeWallets.length} failed: ${result.error}`);
        }

      } catch (error: any) {
        failed++;
        failedWallets.push(wallet.address);
        console.error(`❌ Wallet ${i + 1}/${activeWallets.length} error:`, error);
      }
    }

    // Step 4: Update bundle state
    this.currentBundle.updatedAt = new Date();
    await this.storageService.updateBundle(this.currentBundle);

    if (onProgress) {
      onProgress(100, `Complete: ${successful} success, ${failed} failed`);
    }

    // Step 5: Comprehensive logging
    console.log(`\n📊 Bundle Purchase Summary:`);
    console.log(`✅ Successful: ${successful}/${activeWallets.length}`);
    console.log(`❌ Failed: ${failed}/${activeWallets.length}`);
    console.log(`📈 Success Rate: ${((successful / activeWallets.length) * 100).toFixed(1)}%`);
    
    if (successfulTxs.length > 0) {
      console.log(`\n🔗 Successful transactions:`);
      successfulTxs.forEach(tx => console.log(`   ${tx}`));
    }
    
    if (failedWallets.length > 0) {
      console.log(`\n⚠️ Failed wallets:`);
      failedWallets.forEach(wallet => console.log(`   ${wallet.slice(0,8)}...`));
    }

    // Step 6: Return more accurate success criteria
    const successRate = successful / activeWallets.length;
    const isSuccessful = successRate >= 0.8; // Consider successful if 80%+ wallets succeed

    return {
      success: isSuccessful,
      successful,
      failed,
      transactions: successfulTxs,
      error: !isSuccessful ? `Low success rate: ${(successRate * 100).toFixed(1)}%` : undefined
    };

  } catch (error: any) {
    console.error('Bundle purchase error:', error);
    return {
      success: false,
      successful,
      failed,
      transactions,
      error: error.message
    };
  }
}

// Helper method to refresh wallet balances before execution
async refreshWalletBalances(): Promise<void> {
  if (!this.currentBundle) return;

  for (const wallet of this.currentBundle.wallets) {
    try {
      const pubkey = new PublicKey(wallet.address);
      const balance = await this.connection.getBalance(pubkey);
      wallet.balance = balance / LAMPORTS_PER_SOL;
    } catch (error) {
      console.error(`Failed to refresh balance for wallet ${wallet.address}:`, error);
      wallet.balance = 0; // Mark as unfunded if can't get balance
      wallet.active = false;
    }
  }
}

// Helper method with retry logic for individual swaps
async executeSwapWithRetry(
  keypair: Keypair,
  tokenMint: string,
  amountInSol: number,
  slippage: number,
  maxRetries: number = 3
): Promise<{ success: boolean; signature?: string; error?: string }> {
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 Wallet ${keypair.publicKey.toString().slice(0,8)} - Attempt ${attempt}/${maxRetries}`);
      
      const result = await this.tradingService.swapTokens(
        keypair,
        tokenMint,
        amountInSol,
        slippage
      );

      if (result.success && result.signature) {
        return result;
      }

      // If not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        const retryDelay = attempt * 1000; // Increasing delay: 1s, 2s, 3s
        console.log(`⏳ Retrying in ${retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }

    } catch (error: any) {
      console.error(`Swap attempt ${attempt} failed:`, error);
      
      if (attempt === maxRetries) {
        return { success: false, error: error.message };
      }
    }
  }

  return { success: false, error: 'Max retries exceeded' };
}

  async getDecryptedWallets(bundleId: string): Promise<Keypair[]> {
    if (!this.currentBundle || this.currentBundle.id !== bundleId) {
      throw new Error('Bundle not found or not active');
    }
    
    if (this.isLocked) {
      throw new Error('Bundle is locked. Please unlock first.');
    }
    
    const keypairs: Keypair[] = [];
    for (const wallet of this.currentBundle.wallets) {
      const keypair = this.decryptedWallets.get(wallet.address);
      if (keypair) {
        keypairs.push(keypair);
      }
    }
    
    if (keypairs.length === 0) {
      throw new Error('No decrypted wallets found. Bundle may need to be unlocked.');
    }
    
    return keypairs;
  }

  async withdrawAllFunds(
    mainWallet: PublicKey | string,
    onProgress?: (progress: number) => void
  ): Promise<{
    success: boolean;
    totalWithdrawn: number;
    failed: number;
    error?: string;
  }> {
    if (!this.currentBundle) {
      throw new Error('No active bundle');
    }

    if (this.isLocked) {
      throw new Error('Bundle is locked. Please unlock first.');
    }

    let totalWithdrawn = 0;
    let failed = 0;

    try {
      const mainWalletPubkey = typeof mainWallet === 'string' ? new PublicKey(mainWallet) : mainWallet;
      const fundedWallets = this.currentBundle.wallets.filter(w => w.funded && w.balance > 0);
      
      for (let i = 0; i < fundedWallets.length; i++) {
        const wallet = fundedWallets[i];
        
        try {
          const keypair = this.decryptedWallets.get(wallet.address);
          if (!keypair) {
            throw new Error(`Wallet ${wallet.address} not decrypted`);
          }

          const balance = await this.connection.getBalance(keypair.publicKey);
          if (balance <= 5000) continue;

          const transaction = new Transaction().add(
            SystemProgram.transfer({
              fromPubkey: keypair.publicKey,
              toPubkey: mainWalletPubkey,
              lamports: balance - 5000
            })
          );

          const { blockhash } = await this.connection.getLatestBlockhash();
          transaction.recentBlockhash = blockhash;
          transaction.feePayer = keypair.publicKey;

          transaction.sign(keypair);
          const signature = await this.connection.sendRawTransaction(transaction.serialize());
          await this.connection.confirmTransaction(signature, 'confirmed');

          const withdrawn = (balance - 5000) / LAMPORTS_PER_SOL;
          totalWithdrawn += withdrawn;
          
          wallet.balance = 0;
          wallet.funded = false;

          if (onProgress) {
            onProgress((i + 1) / fundedWallets.length * 100);
          }
        } catch (error) {
          console.error(`Failed to withdraw from wallet ${wallet.address}:`, error);
          failed++;
        }
      }

      this.currentBundle.totalBalance = 0;
      this.currentBundle.updatedAt = new Date();
      await this.storageService.updateBundle(this.currentBundle);

      return { success: true, totalWithdrawn, failed };
    } catch (error: any) {
      return { success: false, totalWithdrawn, failed, error: error.message };
    }
  }

  async unlockBundle(bundleId: string, password: string): Promise<Bundle> {
  const bundle = await this.storageService.getBundle(bundleId);
  if (!bundle) {
    throw new Error('Bundle not found');
  }

  // Check if already unlocked in session
  const sessionActive = sessionStorage.getItem(`bundle_active_${bundleId}`);
  if (sessionActive === 'true' && this.currentBundle?.id === bundleId && !this.isLocked) {
    console.log('Bundle already unlocked in session');
    return this.currentBundle;
  }

  // Decrypt all wallet private keys
  this.decryptedWallets.clear();
  
  for (const wallet of bundle.wallets) {
    try {
      const decryptedKey = await this.encryptionService.decrypt(
        wallet.encryptedPrivateKey,
        password
      );
      const keypair = Keypair.fromSecretKey(bs58.decode(decryptedKey));
      this.decryptedWallets.set(wallet.address, keypair);
    } catch (error) {
      throw new Error('Invalid password');
    }
  }

  this.currentBundle = bundle;
  this.currentPassword = password; // ✅ STORE THE PASSWORD
  this.isLocked = false;
  this.resetInactivityTimer();
  
  // Save to session for persistence
  await this.saveSessionData(bundleId, this.decryptedWallets);

  // Refresh balances
  await this.refreshBalances();

  return bundle;
}

  lockBundle(): void {
  const bundleId = this.currentBundle?.id;
  this.decryptedWallets.clear();
  this.currentPassword = null; // ✅ CLEAR THE PASSWORD
  this.isLocked = true;
  this.currentBundle = null;
  
  // Clear session data
  if (bundleId) {
    sessionStorage.removeItem(`bundle_session_${bundleId}`);
    sessionStorage.removeItem(`bundle_active_${bundleId}`);
  }
  sessionStorage.removeItem('current_bundle_id');
}

  async refreshBalances(): Promise<void> {
    if (!this.currentBundle || this.isLocked) {
      return;
    }

    let totalBalance = 0;
    
    for (const wallet of this.currentBundle.wallets) {
      try {
        const balance = await this.connection.getBalance(new PublicKey(wallet.address));
        wallet.balance = balance / LAMPORTS_PER_SOL;
        wallet.funded = balance > 0;
        totalBalance += wallet.balance;
      } catch (error) {
        console.error(`Failed to refresh balance for ${wallet.address}:`, error);
      }
    }

    if (this.currentBundle) {
      this.currentBundle.totalBalance = totalBalance;
      this.currentBundle.updatedAt = new Date();
      await this.storageService.updateBundle(this.currentBundle);
    }
  }

  async exportBundle(bundleId: string, includePrivateKeys: boolean = false, password?: string): Promise<string> {
    const bundle = await this.storageService.getBundle(bundleId);
    if (!bundle) {
      throw new Error('Bundle not found');
    }

    const exportData: any = {
      ...bundle,
      exportedAt: new Date().toISOString(),
      version: '1.0.0'
    };

    if (includePrivateKeys && password) {
      const decryptedWallets = [];
      for (const wallet of bundle.wallets) {
        try {
          const decryptedKey = await this.encryptionService.decrypt(
            wallet.encryptedPrivateKey,
            password
          );
          decryptedWallets.push({
            ...wallet,
            privateKey: decryptedKey
          });
        } catch (error) {
          throw new Error('Invalid password');
        }
      }
      exportData.wallets = decryptedWallets;
      exportData.encrypted = false;
    } else {
      exportData.encrypted = true;
    }

    return JSON.stringify(exportData, null, 2);
  }

  async importBundle(jsonData: string, password: string): Promise<Bundle> {
    const data = JSON.parse(jsonData);
    
    if (!data.encrypted) {
      const wallets: BundlerWallet[] = [];
      
      for (const wallet of data.wallets) {
        const encryptedPrivateKey = await this.encryptionService.encrypt(
          wallet.privateKey,
          password
        );
        
        wallets.push({
          ...wallet,
          encryptedPrivateKey,
          privateKey: undefined
        });
      }
      
      data.wallets = wallets;
    }

    const bundle: Bundle = {
      ...data,
      id: this.generateBundleId(),
      createdAt: new Date(data.createdAt),
      updatedAt: new Date()
    };

    await this.storageService.saveBundle(bundle);
    return bundle;
  }

  async getUserBundles(owner: string): Promise<Bundle[]> {
    try {
      const bundlesFromDB = await this.storageService.getBundlesByOwner(owner);
      console.log('Got bundles from IndexedDB:', bundlesFromDB.length);
      return bundlesFromDB || [];
    } catch (error) {
      console.error('Failed to get user bundles:', error);
      
      try {
        const stored = localStorage.getItem('wallet_bundles');
        if (stored) {
          const allBundles = JSON.parse(stored);
          const userBundles = allBundles.filter((b: Bundle) => b.owner === owner);
          console.log('Fallback: Got bundles from localStorage:', userBundles.length);
          return userBundles;
        }
      } catch (e) {
        console.error('localStorage fallback also failed:', e);
      }
      
      return [];
    }
  }

  async deleteBundle(bundleId: string): Promise<void> {
    try {
      console.log('Starting delete for bundle:', bundleId);
      
      if (this.currentBundle?.id === bundleId) {
        this.lockBundle();
      }
      
      await this.storageService.deleteBundle(bundleId);
      
      try {
        const stored = localStorage.getItem('wallet_bundles');
        if (stored) {
          const bundles = JSON.parse(stored);
          const filtered = bundles.filter((b: any) => b.id !== bundleId);
          localStorage.setItem('wallet_bundles', JSON.stringify(filtered));
        }
      } catch (error) {
        console.log('Could not clean up localStorage:', error);
      }
      
      localStorage.removeItem(`bundle_${bundleId}_encrypted`);
      
      console.log(`Bundle ${bundleId} deleted successfully from storage`);
    } catch (error) {
      console.error('Failed to delete bundle:', error);
      throw new Error('Failed to delete bundle from storage');
    }
  }

  private getStoredBundles(): any[] {
    try {
      const stored = localStorage.getItem('wallet_bundles');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to get stored bundles:', error);
      return [];
    }
  }

  private startInactivityTimer(): void {
    this.resetInactivityTimer();
    
    ['mousedown', 'keydown', 'touchstart', 'mousemove'].forEach(event => {
      document.addEventListener(event, () => this.resetInactivityTimer());
    });
  }

  private resetInactivityTimer(): void {
    clearTimeout(this.inactivityTimer);
    
    if (!this.isLocked) {
      this.inactivityTimer = setTimeout(() => {
        this.lockBundle();
        console.log('Bundle auto-locked due to inactivity');
      }, 5 * 60 * 1000);
    }
  }

  private generateBundleId(): string {
    return `bundle-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async checkConnection(): Promise<boolean> {
    try {
      await this.connection.getVersion();
      return true;
    } catch {
      return false;
    }
  }

  getRpcEndpoint(): string {
    return this.connection.rpcEndpoint;
  }
}

// Export singleton instance
export const bundlerService = new FrontendBundlerService();